// src/components/Home.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL, SOCKET_URL } from '../config';
import { apiFetch, getAccessToken } from '../utils/auth';
import { getRangiDeviceId } from '../utils/deviceId';
import { useRole, useEmployeeId } from '../context/RoleContext';

// стили новостей
import s from './News.module.css';
// тапбар админки
import ap from './AdminPanel.module.css';

import { ReactComponent as AppLogo } from '../icons/app_black_logo.svg';
// модалка создания/редактирования новостей
import NewsEditorModal from './NewsEditorModal';
import { Textarea, ActionsMenu, ActionsMenuDotsIcon, DropdownMenu, EmployeeTaskStatsAvatar } from './UI';
import { useEmployeeTaskStats } from '../utils/employeeTaskStats';
import actionsMenuStyles from './UI/ActionsMenu.module.css';
// берём готовые стили FAB из админки
import sAdmin from './AdminPanel.module.css';
// иконки пузырика и плюса — как в админке
import { ReactComponent as FabBubble } from '../icons/circle.svg';
import { ReactComponent as NewsPlusIcon } from '../icons/add_icon_plus.svg';
import { ReactComponent as ConfirmEditIcon } from '../icons/confirm_edit.svg';
import { ReactComponent as SendMessageIcon } from '../icons/send_message_icon.svg';

import DOMPurify from 'dompurify';
import { useMobileKeyboardAvoidance } from '../utils/mobileKeyboardAvoidance';


// 🔌 Socket.IO
import { io } from 'socket.io-client';

const NEWS_MENU_OUTSIDE_SELECTOR = [
  s.commentMenuWrap,
  actionsMenuStyles.wrap,
].join(', ');

const API_BASE = (API_URL || '').replace(/\/api\/v\d+\/?$/, '');
function resolveImageUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_BASE}${path}`;
}

async function postToSW(message) {
  try {
    const reg = await navigator.serviceWorker?.ready;
    const sw = reg?.active || reg?.waiting || reg?.installing;
    if (sw) sw.postMessage(message);
    else navigator.serviceWorker?.controller?.postMessage(message); // fallback
  } catch { }
}

// ——— Уведомить service worker (очистка бейджа/закрытие уведомлений и пр.) ———
function notifySW(type, payload = {}) {
  postToSW({ type, ...payload });
}

async function syncBadge() {
  try {
    const r = await apiFetch(`${API_URL}/chats/badge_total`);
    if (!r.ok) return;
    const data = await r.json();
    const count = data?.badge_count ?? 0;
    await postToSW({ type: "SET_BADGE", count });
  } catch { }
}

function formatCommentTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const time = d.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    const dayStart = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const dDay = dayStart(d);
    const today = dayStart(now);

    if (dDay === today) return `сегодня ${time}`;

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (dDay === dayStart(yesterday)) return `вчера ${time}`;

    return d.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

// ширина карточки в горизонтальной ленте (используется и как snap padding)
const RUBRIC_CARD_W = 180;

function orientationFromImgLoad(ev) {
  const { naturalWidth: w, naturalHeight: h } = ev.currentTarget;
  if (!w || !h) return 'landscape';
  return h > w ? 'portrait' : 'landscape';
}

function Home() {
  const role = useRole();
  const employeeId = useEmployeeId();
  const location = useLocation();
  const navigate = useNavigate();

  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMoreNews, setHasMoreNews] = useState(true);
  const [nextNewsOffset, setNextNewsOffset] = useState(0);
  const NEWS_PAGE_LIMIT = 50;

  const [rubricStrips, setRubricStrips] = useState([]);
  const [allRubrics, setAllRubrics] = useState([]);

  // выбранная рубрика (для табов)
  const [activeRubricId, setActiveRubricId] = useState(null);
  useEffect(() => {
    if (allRubrics.length && activeRubricId == null) {
      setActiveRubricId(allRubrics[0].id);
    }
  }, [allRubrics, activeRubricId]);
  const activeBlock = useMemo(
    () => rubricStrips.find((b) => b?.rubric?.id === activeRubricId) || null,
    [rubricStrips, activeRubricId]
  );
  const activeIndex = Math.max(0, allRubrics.findIndex(r => r.id === activeRubricId));

  // --- главная новость ---
  const [mainNews, setMainNews] = useState(null);

  // --- ТОП новости (горизонтальная лента под главной) ---
  const [topCards, setTopCards] = useState([]);

  const [heroOrient, setHeroOrient] = useState('landscape');
  const [listThumbOrient, setListThumbOrient] = useState({});
  const [stripThumbOrient, setStripThumbOrient] = useState({});
  const [modalHeroOrient, setModalHeroOrient] = useState('landscape');

  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const isAdmin = role === 'admin';

  // ==== редактор новостей (модалка) ====
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create'); // 'create' | 'edit'
  const [editorItem, setEditorItem] = useState(null);

  // Лочим фон position: fixed
  useEffect(() => {
    const body = document.body;

    if (modalOpen) {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';

      return () => {
        // очистка и восстановление при закрытии модалки ИЛИ размонтировании Home
        body.style.position = '';
        const top = body.style.top;
        body.style.top = '';
        body.style.width = '';
        window.scrollTo(0, top ? -parseInt(top, 10) : 0);
      };
    }

    // если modalOpen === false — на всякий случай тоже возвращаем в норму
    body.style.position = '';
    const top = body.style.top;
    body.style.top = '';
    body.style.width = '';
    window.scrollTo(0, top ? -parseInt(top, 10) : 0);

    return () => { };
  }, [modalOpen]);


  useEffect(() => {
    if (modalOpen && selected && window.innerWidth >= 768) {
      loadComments(selected.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, selected?.id]);

  useEffect(() => {
    setHeroOrient('landscape');
  }, [mainNews?.id, mainNews?.image_url]);

  useEffect(() => {
    setModalHeroOrient('landscape');
  }, [selected?.id, selected?.image_url]);

  // ===== Счётчики комментариев по новостям (для вертикальной ленты) =====
  const [commentCounts, setCommentCounts] = useState({}); // { [newsId]: number }
  // Персональные непрочитанные
  const [unreadMap, setUnreadMap] = useState({}); // { [newsId]: number }
  // Курсор «до какого комментария прочитано» в открытой новости
  const [myLastReadId, setMyLastReadId] = useState(null);
  // ======================================================================
  const loadMoreTriggerRef = useRef(null);
  const loadNewsRef = useRef(null);
  const newsRequestInFlightRef = useRef(false);
  const lastAppendOffsetRef = useRef(null);

  const loadUnreadCounts = async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    try {
      const qs = `?ids=${ids.join('&ids=')}`;
      const r = await apiFetch(`${API_URL}/news/comments/unread_counts${qs}`);
      if (r.ok) {
        const map = await r.json(); // { [newsId]: number }
        setUnreadMap(prev => ({ ...prev, ...map }));
      }
    } catch { }
  };

  const loadCommentCounts = async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    try {
      const qs = `?ids=${ids.join('&ids=')}`;
      const r = await apiFetch(`${API_URL}/news/comments/counts${qs}`);
      if (r.ok) {
        const map = await r.json(); // { [newsId]: number }
        setCommentCounts((prev) => ({ ...prev, ...map }));
      }
    } catch {
      /* если эндпоинт не поддерживает ids — тихо игнорируем */
    }
  };

  const loadNews = async ({ append = false, offset = 0 } = {}) => {
    if (newsRequestInFlightRef.current) return;
    if (append && (!hasMoreNews || lastAppendOffsetRef.current === offset)) return;

    newsRequestInFlightRef.current = true;
    if (append) lastAppendOffsetRef.current = offset;

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
        lastAppendOffsetRef.current = null;
      }

      const res = await apiFetch(`${API_URL}/news?limit=${NEWS_PAGE_LIMIT}&offset=${offset}`);
      if (!res.ok) throw new Error('Network response was not ok');
      const data = await res.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      const rawHasMore = Boolean(data?.has_more);
      const nextOffset = Number.isFinite(data?.next_offset)
        ? Number(data.next_offset)
        : (offset + items.length);
      const paginationAdvanced = nextOffset > offset;
      const hasMore = rawHasMore && items.length > 0 && paginationAdvanced;

      setNews((prev) => {
        if (!append) return items;

        const seen = new Set(prev.map((n) => String(n.id)));
        const uniqueItems = items.filter((n) => !seen.has(String(n?.id)));
        return uniqueItems.length ? [...prev, ...uniqueItems] : prev;
      });
      setHasMoreNews(hasMore);
      setNextNewsOffset(nextOffset);

      // инициализируем счётчики, если backend присылает поле comments_count
      const counts = {};
      items.forEach((n) => {
        if (typeof n?.comments_count === 'number') counts[n.id] = n.comments_count;
      });
      if (Object.keys(counts).length) {
        setCommentCounts((prev) => ({ ...prev, ...counts }));
      }

      // подтягиваем только для id новых новостей
      const ids = items.map((n) => n.id).filter((id) => id != null);
      await Promise.all([
        loadUnreadCounts(ids),
        loadCommentCounts(ids),
      ]);
    } catch (e) {
      setError(e.message || 'Ошибка загрузки новостей');
      if (append) {
        setHasMoreNews(false);
      }
    } finally {
      newsRequestInFlightRef.current = false;
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  };
  loadNewsRef.current = loadNews;

  const loadRubrics = async () => {
    try {
      const [stripsRes, allRes] = await Promise.all([
        apiFetch(`${API_URL}/news/rubrics`),
        apiFetch(`${API_URL}/news/rubrics/all`)
      ]);
      const strips = stripsRes.ok ? await stripsRes.json() : [];
      const all = allRes.ok ? await allRes.json() : [];

      // сортируем по приоритету (order), затем по id как тай-брейк
      const sortByOrder = (a, b) =>
        (Number(a?.order ?? a?.rubric?.order ?? 0) - Number(b?.order ?? b?.rubric?.order ?? 0)) ||
        (Number(a?.id ?? a?.rubric?.id ?? 0) - Number(b?.id ?? b?.rubric?.id ?? 0));

      const sortedAll = (Array.isArray(all) ? all.slice() : []).sort(sortByOrder);
      const sortedStrips = (Array.isArray(strips) ? strips.slice() : []).sort(sortByOrder);

      setRubricStrips(sortedStrips);
      setAllRubrics(sortedAll);
    } catch {
      /* ignore */
    }
  };


  // загрузка главной новости
  const loadMain = async () => {
    try {
      const r = await apiFetch(`${API_URL}/news/main`);
      if (r.ok) setMainNews(await r.json());
      else setMainNews(null);
    } catch {
      setMainNews(null);
    }
  };

  // загрузка ТОП новостей
  const loadTop = async () => {
    try {
      const r = await apiFetch(`${API_URL}/news/top?limit=10`);
      const list = r.ok ? await r.json() : [];
      setTopCards(Array.isArray(list) ? list : []);
    } catch {
      setTopCards([]);
    }
  };

  useEffect(() => {
    loadNewsRef.current?.({ append: false, offset: 0 });
    loadRubrics();
    loadMain(); // ← подгружаем «героя»
    loadTop();  // ← подгружаем «ТОП»
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const node = loadMoreTriggerRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting && !loading && !loadingMore && hasMoreNews) {
          loadNewsRef.current?.({ append: true, offset: nextNewsOffset });
        }
      },
      { root: null, rootMargin: '300px 0px', threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMoreNews, nextNewsOffset, loading, loadingMore]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get('open');
    if (openId) {
      const idNum = Number(openId);
      if (!Number.isNaN(idNum)) {
        if (!selected || selected.id !== idNum) {
          openNews(idNum);
        }
      }
    } else {
      // если параметра open нет (например, пользователь нажал «назад») —
      // закрываем модалку и сбрасываем выбранную новость
      if (modalOpen || selected) {
        setModalOpen(false);
        setSelected(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ====== ВКЛАДКИ И КОММЕНТАРИИ ======
  const [activeTab, setActiveTab] = useState('news'); // 'news' | 'comments'
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const socketRef = useRef(null);

  // ▼▼▼ Состояние и хелперы для меню/редактирования комментариев ▼▼▼
  const [commentMenuId, setCommentMenuId] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentBusy, setEditingCommentBusy] = useState(false);
  const commentInputRef = useRef(null);
  const newsModalRef = useRef(null);
  const newsComposerRef = useRef(null);
  // меню «карандаш» для новости
  const [newsMenuId, setNewsMenuId] = useState(null);
  const openNewsMenu = (id) => setNewsMenuId((prev) => (prev === id ? null : id));

  const handleEditNewsFromMenu = (item) => {
    setNewsMenuId(null);
    openEditNews(item);
  };
  const deleteNewsItem = async (item) => {
    setNewsMenuId(null);
    if (!window.confirm('Удалить новость?')) return;
    try {
      const r = await apiFetch(`${API_URL}/news/${item.id}`, { method: 'DELETE' });
      if (r.ok) {
        // локально убираем из всех лент
        setNews((prev) => prev.filter((n) => String(n.id) !== String(item.id)));
        setTopCards((prev) => prev.filter((n) => String(n.id) !== String(item.id)));
        setRubricStrips((prev) =>
          prev.map((b) => ({ ...b, items: (b.items || []).filter((n) => String(n.id) !== String(item.id)) }))
        );
        if (mainNews && String(mainNews.id) === String(item.id)) setMainNews(null);
      }
    } catch { }
  };

  const isAuthor = (c) => c?.author_id && c.author_id === employeeId;
  const canEditComment = (c) => isAuthor(c);                  // только автор
  const canDeleteComment = (c) => isAuthor(c) || role === 'admin'; // автор или админ

  const openCommentMenu = (id) =>
    setCommentMenuId(prev => (prev === id ? null : id));

  const editingComment = useMemo(() => {
    if (!editingCommentId) return null;
    return comments.find((c) => String(c.id) === String(editingCommentId)) ?? null;
  }, [comments, editingCommentId]);

  const commentAuthorIds = useMemo(
    () => [...new Set(
      comments
        .map((c) => Number(c.author_id))
        .filter((id) => Number.isFinite(id) && id > 0)
    )],
    [comments]
  );
  const commentAuthorStatsMap = useEmployeeTaskStats(commentAuthorIds);

  const startEditComment = (c) => {
    if (!c?.id || editingCommentBusy) return;
    setCommentMenuId(null);
    setEditingCommentId(c.id);
    setCommentText(c.content || '');
    window.setTimeout(() => {
      document.getElementById(`news-comment-${c.id}`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      focusNewsCommentInput();
    }, 0);
  };

  const cancelEditComment = () => {
    if (editingCommentBusy) return;
    setEditingCommentId(null);
    setCommentText('');
  };

  const saveCommentEdit = async () => {
    const comment = editingComment;
    const text = commentText.trim();
    if (!selected || !comment?.id || editingCommentBusy || !text) return;
    if (text === (comment.content || '').trim()) {
      cancelEditComment();
      return;
    }

    setEditingCommentBusy(true);
    try {
      const r = await apiFetch(`${API_URL}/news/${selected.id}/comments/${comment.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: text }),
      });
      if (r.ok) {
        const updated = await r.json();
        setComments((prev) => prev.map((x) => (String(x.id) === String(updated.id) ? updated : x)));
        setEditingCommentId(null);
        setCommentText('');
      }
    } catch { } finally {
      setEditingCommentBusy(false);
    }
  };

  // ▲▲▲ конец добавленного блока ▲▲▲

  // ▼▼▼ Эффект: закрывать меню комментария/новости по клику вне/по Esc ▼▼▼
  useEffect(() => {
    const handleOutside = (e) => {
      if (commentMenuId == null && newsMenuId == null) return;
      if (!e.target.closest?.(NEWS_MENU_OUTSIDE_SELECTOR)) {
        if (commentMenuId != null) setCommentMenuId(null);
        if (newsMenuId != null) setNewsMenuId(null);
      }
    };

    // слушаем обычный click без capture, чтобы сначала сработали onClick внутри меню
    document.addEventListener('click', handleOutside);

    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (commentMenuId != null) setCommentMenuId(null);
      if (newsMenuId != null) setNewsMenuId(null);
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('click', handleOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [commentMenuId, newsMenuId]);

  useMobileKeyboardAvoidance({
    active: modalOpen && activeTab === 'comments',
    modalRef: newsModalRef,
    composerRef: newsComposerRef,
  });

  // подключение сокета
  useEffect(() => {
    if (!employeeId) return;

    const socket = io(SOCKET_URL, {
      path: '/ws/socket.io',
      transports: ['websocket'],
      withCredentials: true,
      auth: (cb) => {
        cb({
          token: getAccessToken(),
          device_id: getRangiDeviceId(),
        });
      },
    });
    socketRef.current = socket;

    // 🔔 новая новость опубликована — подтягиваем и добавляем в ленту
    socket.on('news_published', (payload) => {
      if (!payload || !payload.id) return;

      // Дотянуть полную новость и аккуратно поставить первой в список,
      // избегая дублей, если она уже есть в состоянии.
      apiFetch(`${API_URL}/news/${payload.id}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((full) => {
          if (!full) return;
          setNews((prev) =>
            prev.some((n) => String(n.id) === String(full.id))
              ? prev
              : [full, ...prev]
          );
          // Опционально синхронизируем счётчик комментариев, если его нет
          setCommentCounts((prev) => ({
            ...prev,
            [full.id]: prev[full.id] ?? (full.comments_count ?? 0),
          }));
        })
        .catch(() => { });
      // Актуализируем топ-ленту на всякий случай
      loadTop();
    });

    // новые комментарии → в текущую открытую новость (список)
    socket.on('news_comment_added', (payload) => {
      if (!payload || !payload.news_id || !payload.comment) return;

      const isSameNews =
        selected && String(payload.news_id) === String(selected.id);
      const onCommentsTab = activeTab === 'comments' || window.innerWidth >= 768;
      const isMine =
        payload.comment.author_id != null &&
        employeeId != null &&
        String(payload.comment.author_id) === String(employeeId);

      if (isSameNews) {
        // всегда подмешиваем комментарий в список открытой новости
        setComments((prev) =>
          prev.some((x) => String(x.id) === String(payload.comment.id))
            ? prev
            : [payload.comment, ...prev]
        );

        if (onCommentsTab) {
          // на вкладке "Комментарии" — сразу читаем до этого id
          apiFetch(`${API_URL}/news/${payload.news_id}/comments/read`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ up_to_comment_id: payload.comment.id }),
          }).catch(() => { });
          setUnreadMap((prev) => ({ ...prev, [payload.news_id]: 0 }));
          setMyLastReadId((prev) => Math.max(prev ?? 0, payload.comment.id));
        } else {
          // на вкладке "Новость" — накапливаем непрочитанные (кроме своих)
          if (!isMine) {
            setUnreadMap((prev) => ({
              ...prev,
              [payload.news_id]: (prev[payload.news_id] || 0) + 1,
            }));
          }
        }
      } else {
        // другая новость — просто увеличиваем непрочитанные (кроме своих)
        if (!isMine) {
          setUnreadMap((prev) => ({
            ...prev,
            [payload.news_id]: (prev[payload.news_id] || 0) + 1,
          }));
        }
      }

      // всегда обновляем общий счётчик для соответствующей новости
      setCommentCounts((prev) => ({
        ...prev,
        [payload.news_id]: (prev[payload.news_id] || 0) + 1,
      }));
    });

    // удаление комментария
    socket.on('news_comment_deleted', (payload) => {
      if (!payload || !payload.news_id || !payload.comment_id) return;

      if (selected && activeTab === 'comments' && String(payload.news_id) === String(selected.id)) {
        setComments((prev) => prev.filter((c) => String(c.id) !== String(payload.comment_id)));
      }

      // скорректировать счётчик для новости
      setCommentCounts((prev) => ({
        ...prev,
        [payload.news_id]: Math.max(0, (prev[payload.news_id] || 0) - 1),
      }));

      // актуализируем персональные непрочитанные точечно
      loadUnreadCounts([payload.news_id]);
    });

    // редактирование комментария — счётчик не меняется
    socket.on('news_comment_edited', (payload) => {
      if (!payload || !payload.news_id || !payload.comment) return;
      if (selected && activeTab === 'comments' && String(payload.news_id) === String(selected.id)) {
        setComments((prev) =>
          prev.map((x) =>
            String(x.id) === String(payload.comment.id) ? payload.comment : x
          )
        );
      }
    });

    // кто-то пометил прочитанным — если это текущий пользователь, обнулим счётчик по новости
    socket.on('news_comments_read', (payload) => {
      if (!payload || !payload.news_id) return;
      if (payload.user_id && employeeId && String(payload.user_id) === String(employeeId)) {
        setUnreadMap(prev => ({ ...prev, [payload.news_id]: 0 }));
        if (selected && String(selected.id) === String(payload.news_id)) {
          setMyLastReadId(payload.last_read_comment_id ?? myLastReadId);
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [selected, activeTab, employeeId, myLastReadId]);


  const loadComments = async (newsId) => {
    try {
      setCommentsLoading(true);
      // расширенный ответ: список + мой курсор
      const r = await apiFetch(`${API_URL}/news/${newsId}/comments/full`);
      const full = r.ok ? await r.json() : { comments: [] };
      const list = Array.isArray(full.comments) ? full.comments : [];

      // UI ожидает "новые сверху" — разворачиваем
      setComments(list.slice().reverse());

      setMyLastReadId(full.my_last_read_comment_id ?? null);

      // сразу пометить прочитанным до последнего (верхнего в UI)
      const lastShown = list.length ? list[list.length - 1].id : null; // последний по возрастанию
      if (lastShown != null) {
        apiFetch(`${API_URL}/news/${newsId}/comments/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ up_to_comment_id: lastShown }),
        }).then(() => {
          setUnreadMap(prev => ({ ...prev, [newsId]: 0 }));
          setMyLastReadId(lastShown);
        }).catch(() => { });
      }

      // актуализируем общий счётчик фактом
      if (Array.isArray(list)) {
        setCommentCounts((prev) => ({ ...prev, [newsId]: list.length }));
      }
    } catch {
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const sendComment = async (e) => {
    e.preventDefault();
    if (editingCommentId) {
      await saveCommentEdit();
      return;
    }
    if (!selected || !commentText.trim()) return;
    try {
      const r = await apiFetch(`${API_URL}/news/${selected.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: commentText.trim() }),
      });
      if (r.ok) {
        const created = await r.json();
        // оптимистично добавляем в список (счетчик обновится по сокету)
        setComments((prev) =>
          prev.some((x) => String(x.id) === String(created.id)) ? prev : [created, ...prev]
        );
        setCommentText('');

        // и сразу считаем прочитанным
        apiFetch(`${API_URL}/news/${selected.id}/comments/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ up_to_comment_id: created.id }),
        }).catch(() => { });
        setUnreadMap(prev => ({ ...prev, [selected.id]: 0 }));
        setMyLastReadId(prev => Math.max(prev ?? 0, created.id));
      }
    } catch { }
  };

  const handleCommentKeyDown = (e) => {
    if (editingCommentId) {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelEditComment();
        return;
      }
      if (e.key !== 'Enter' || e.shiftKey) return;
      e.preventDefault();
      if (editingCommentBusy || !commentText.trim()) return;
      saveCommentEdit();
      return;
    }
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    if (!commentText.trim()) return;
    sendComment(e);
  };

  const focusNewsCommentInput = () => {
    const footers = document.querySelectorAll(`.${s.commentComposerFooter}`);
    for (const footer of footers) {
      if (footer.offsetParent === null) continue;
      const el = footer.querySelector('textarea');
      if (!el) continue;
      el.focus();
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch { /* ignore */ }
      return;
    }
    const fallback = commentInputRef.current;
    if (!fallback) return;
    fallback.focus();
  };

  const renderNewsCommentComposer = (footerClassName = '', footerRef = null) => (
    <div
      ref={footerRef}
      className={[s.commentComposerFooter, footerClassName].filter(Boolean).join(' ')}
    >
      {editingCommentId && (
        <div className={s.commentEditComposerBar}>
          <span className={s.commentEditComposerLabel}>Редактирование</span>
          <button
            type="button"
            className={s.commentEditComposerCancel}
            onClick={cancelEditComment}
            disabled={editingCommentBusy}
          >
            Отмена
          </button>
        </div>
      )}

      <form
        className={`${s.commentForm} ${editingCommentId ? s.commentFormEditing : ''}`}
        onSubmit={sendComment}
      >
        <Textarea
          wrapperClassName={`${s.commentInput} ${editingCommentId ? s.commentInputEditing : ''}`}
          placeholder={editingCommentId ? 'Редактировать комментарий…' : 'Напишите что-нибудь…'}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          onKeyDown={handleCommentKeyDown}
          textareaRef={commentInputRef}
          ariaLabel={editingCommentId ? 'Редактировать комментарий' : 'Комментарий к новости'}
          rows={editingCommentId ? 2 : 3}
        />
        <button
          type="submit"
          className={s.commentSend}
          disabled={editingCommentBusy || !commentText.trim()}
          aria-label={editingCommentId ? 'Сохранить изменения' : 'Отправить комментарий'}
          title={editingCommentId ? 'Сохранить' : 'Отправить'}
        >
          {editingCommentId ? (
            <ConfirmEditIcon className={s.commentSendIcon} aria-hidden="true" focusable="false" />
          ) : (
            <SendMessageIcon className={s.commentSendIcon} aria-hidden="true" focusable="false" />
          )}
        </button>
      </form>
    </div>
  );

  const deleteComment = async (c) => {
    if (!selected) return;
    setCommentMenuId(null); // закрыть меню перед удалением
    if (!window.confirm('Удалить комментарий?')) return;
    try {
      const r = await apiFetch(`${API_URL}/news/${selected.id}/comments/${c.id}`, { method: 'DELETE' });
      if (r.ok) {
        setComments((prev) => prev.filter((x) => x.id !== c.id));
        if (String(editingCommentId) === String(c.id)) {
          setEditingCommentId(null);
          setCommentText('');
        }
      }
    } catch { }
  };
  // ===================================

  // === открытие модалки новости ===
  const openNews = async (id) => {
    try {
      const res = await apiFetch(`${API_URL}/news/${id}`);
      if (!res.ok) throw new Error('Не удалось загрузить новость');
      const data = await res.json();

      setSelected(data);
      setModalOpen(true);
      setActiveTab('news');
      setComments([]);
      setCommentText('');
      setEditingCommentId(null);

      // ❌ навигации тут больше НЕТ
      // navigate({ pathname: location.pathname, search: `?open=${id}` }, { replace: false });

      // пометка прочитанной и очистка badge — как было
      await apiFetch(`${API_URL}/news/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ news_id: id }),
      });

      navigator.serviceWorker?.controller?.postMessage({
        type: 'CLEAR_BADGE_TAG',
        tag: `news-${id}`,
      });
      await syncBadge();
    } catch (e) {
      console.error(e);
    }
  };

  const openNewsByUrl = (id) => {
    navigate(
      { pathname: location.pathname, search: `?open=${id}` },
      { replace: false } // важно: именно push, чтобы /news остался в истории
    );
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelected(null);
    setEditingCommentId(null);
    setCommentText('');
    navigate(-1); // шаг назад по истории: либо на /news, либо на /plus
  };

  // ==== открыть модалку создания / редактирования ====
  const openCreateNews = () => {
    setEditorMode('create');
    setEditorItem(null);
    setEditorOpen(true);
  };
  const openEditNews = (item, e) => {
    if (e) e.stopPropagation();
    setEditorMode('edit');
    setEditorItem(item);
    setEditorOpen(true);
  };

  // ====== бесконечный скролл для горизонтальных рубрик/топа ======
  const stripRefs = useRef({});         // rubricId -> HTMLElement
  const teleportingRef = useRef({});    // rubricId -> boolean, защита от повторных телепортов
  const copyWRef = useRef({});          // rubricId -> точная ширина одной "копии" ленты
  const stripMouseDragRef = useRef({
    active: false,
    moved: false,
    startX: 0,
    startScrollLeft: 0,
    el: null,
  });
  const suppressStripCardClickUntilRef = useRef(0);

  const setStripRef = (rubricId) => (el) => {
    if (el) stripRefs.current[rubricId] = el;
  };

  // Инициализация: показываем "начало" средней копии без снапа...
  useEffect(() => {
    const teardowns = [];
    const raf = requestAnimationFrame(() => {
      // инициализируем только активную рубрику и (если есть) ТОП
      const blocks = [
        ...(activeBlock ? [activeBlock] : []),
        ...(topCards && topCards.length ? [{ rubric: { id: '__top__' }, items: topCards }] : []),
      ];

      blocks.forEach((block) => {
        const el = stripRefs.current[block?.rubric?.id];
        if (!el) return;

        const m = Array.isArray(block?.items) ? block.items.length : 0;
        if (m > 0 && el.children.length >= m + 1) {
          const w = el.children[m].offsetLeft - el.children[0].offsetLeft;
          if (w > 0) copyWRef.current[block.rubric.id] = w;
        }

        el.style.scrollSnapType = 'none';
        const one = copyWRef.current[block.rubric.id] ?? el.scrollWidth / 3;
        if (one > 0) el.scrollLeft = one;

        let snapEnabled = false;
        const enableSnap = () => {
          if (snapEnabled) return;
          snapEnabled = true;
          el.style.scrollSnapType = 'x mandatory';
          el.removeEventListener('wheel', onWheel);
          el.removeEventListener('pointerdown', onPointerDown);
          el.removeEventListener('pointermove', onPointerMove);
          el.removeEventListener('pointerup', onPointerUp);
          el.removeEventListener('pointercancel', onPointerUp);
        };

        const onWheel = (e) => {
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 2) {
            enableSnap();
          }
        };

        let startX = 0, startY = 0;
        const H_THRESHOLD = 12;

        const onPointerDown = (e) => {
          startX = e.clientX;
          startY = e.clientY;
          el.addEventListener('pointermove', onPointerMove, { passive: true });
          el.addEventListener('pointerup', onPointerUp, { once: true });
          el.addEventListener('pointercancel', onPointerUp, { once: true });
        };
        const onPointerMove = (e) => {
          const dx = Math.abs(e.clientX - startX);
          const dy = Math.abs(e.clientY - startY);
          if (dx > dy && dx >= H_THRESHOLD) {
            enableSnap();
          }
        };
        const onPointerUp = () => {
          el.removeEventListener('pointermove', onPointerMove);
        };

        el.addEventListener('wheel', onWheel, { passive: true });
        el.addEventListener('pointerdown', onPointerDown, { passive: true });

        teardowns.push(() => {
          el.removeEventListener('wheel', onWheel);
          el.removeEventListener('pointerdown', onPointerDown);
          el.removeEventListener('pointermove', onPointerMove);
          el.removeEventListener('pointerup', onPointerUp);
          el.removeEventListener('pointercancel', onPointerUp);
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf);
      teardowns.forEach((fn) => {
        try { fn(); } catch { }
      });
    };
  }, [rubricStrips, activeRubricId, activeBlock, topCards]);

  const onStripScroll = (rubricId) => {
    const el = stripRefs.current[rubricId];
    if (!el || teleportingRef.current[rubricId]) return;

    const x = el.scrollLeft;
    const THRESH = 24;

    // ширина одной «копии» считаем без округлений, от общего контента
    const one = el.scrollWidth / 3;

    if (x < one - THRESH) {
      teleportingRef.current[rubricId] = true;
      el.classList.add(s.noSnap);                 // на один кадр отключаем snap
      const saved = el.style.scrollBehavior;
      el.style.scrollBehavior = 'auto';
      el.scrollLeft = x + one;                     // телепорт в среднюю копию
      el.style.scrollBehavior = saved;
      requestAnimationFrame(() => {
        el.classList.remove(s.noSnap);             // возвращаем snap
        teleportingRef.current[rubricId] = false;
      });
    } else if (x >= 2 * one + THRESH) {
      teleportingRef.current[rubricId] = true;
      el.classList.add(s.noSnap);
      const saved = el.style.scrollBehavior;
      el.style.scrollBehavior = 'auto';
      el.scrollLeft = x - one;                     // телепорт назад в среднюю копию
      el.style.scrollBehavior = saved;
      requestAnimationFrame(() => {
        el.classList.remove(s.noSnap);
        teleportingRef.current[rubricId] = false;
      });
    }
  };

  const beginStripMouseDrag = (e) => {
    if (e.button !== 0) return;
    const el = e.currentTarget;
    if (!el) return;
    e.preventDefault();

    const state = stripMouseDragRef.current;
    if (state.active) {
      state.active = false;
      state.moved = false;
      state.el = null;
    }
    state.active = true;
    state.moved = false;
    state.startX = e.clientX;
    state.startScrollLeft = el.scrollLeft;
    state.el = el;
    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = el.style.cursor;
    document.body.style.userSelect = 'none';
    el.style.cursor = 'grabbing';

    const onMove = (ev) => {
      const s = stripMouseDragRef.current;
      if (!s.active || !s.el) return;
      const dx = ev.clientX - s.startX;
      if (Math.abs(dx) > 4) s.moved = true;
      s.el.scrollLeft = s.startScrollLeft - dx;
      if (ev.cancelable) ev.preventDefault();
    };

    const finishDrag = () => {
      const s = stripMouseDragRef.current;
      if (s.moved) suppressStripCardClickUntilRef.current = Date.now() + 220;
      s.active = false;
      s.moved = false;
      s.el = null;
      document.body.style.userSelect = prevUserSelect;
      el.style.cursor = prevCursor;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', finishDrag);
      window.removeEventListener('mouseleave', finishDrag);
      window.removeEventListener('blur', finishDrag);
    };

    window.addEventListener('mousemove', onMove, { passive: false });
    window.addEventListener('mouseup', finishDrag, { passive: true, once: true });
    window.addEventListener('mouseleave', finishDrag, { passive: true, once: true });
    window.addEventListener('blur', finishDrag, { passive: true, once: true });
  };
  // ===========================================================

  if (loading) return <div className="loading">Загрузка новостей…</div>;
  const unreadForSelected = selected ? (unreadMap[selected.id] || 0) : 0;

  return (
    <div className={s.page}>
      {error && (
        <div className="error" style={{ marginBottom: 12 }}>
          Ошибка: {error}
        </div>
      )}

      {/* ===== Главная новость (герой) над рубриками ===== */}
      {mainNews && (
        <section
          className={`${s.hero} ${s.fullBleed} ${heroOrient === 'portrait' ? s.heroAspectPortrait : s.heroAspectLandscape}`}
          onClick={() => openNewsByUrl(mainNews.id)}
          role="button"
          title={mainNews.title}
        >
          {mainNews.image_url ? (
            <img
              src={resolveImageUrl(mainNews.image_url)}
              alt={mainNews.title}
              className={s.heroImg}
              loading="lazy"
              onLoad={(e) => setHeroOrient(orientationFromImgLoad(e))}
            />
          ) : (
            <div className={s.heroPh} />
          )}
          <div className={s.heroOverlay}>
            <span className={s.heroBadge}>Главное</span>
            <h2 className={s.heroTitle}>{mainNews.title}</h2>
            <time className={s.heroDate}>
              {mainNews.date ? new Date(mainNews.date).toLocaleDateString() : ''}
            </time>
          </div>
        </section>
      )}

      {/* ===== ТОП новости — горизонтальная лента под главной ===== */}
      {topCards.length > 0 && (
        <section className={s.rubric}>
          <div className={s.rubricTitle}>ТОП новости</div>
          <div
            ref={setStripRef('__top__')}
            className={s.rubricStrip}
            style={{
              display: 'grid',
              gridAutoFlow: 'column',
              gridAutoColumns: `${RUBRIC_CARD_W}px`,
              gap: 0,
              overflowX: 'auto',
              padding: '0 2px 8px',
              scrollSnapType: 'x none',
              scrollPaddingInline: `calc((100% - ${RUBRIC_CARD_W}px) / 2)`
            }}
            onScroll={() => onStripScroll('__top__')}
            onMouseDown={beginStripMouseDrag}
          >
            {[...topCards, ...topCards, ...topCards].map((item, idx) => {
              const img = resolveImageUrl(item.vertical_image_url || item.image_url);
              const date = item.date ? new Date(item.date).toLocaleDateString() : '';
              const key = `top-${item.id}-${idx}`;
              return (
                <div
                  key={key}
                  className={s.rubricCard}
                  onClick={() => {
                    if (Date.now() < suppressStripCardClickUntilRef.current) return;
                    openNewsByUrl(item.id);
                  }}
                  role="button"
                  style={{ scrollSnapAlign: 'center', scrollSnapStop: 'always', cursor: 'pointer' }}
                >
                  <div
                    className={`${s.rubricThumbWrap} ${stripThumbOrient[item.id] === 'portrait' ? s.rubricThumbPortrait : s.rubricThumbLandscape}`}
                    style={{ width: '100%', borderRadius: 3, overflow: 'hidden', background: '#eee' }}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt={item.title}
                        loading='eager'
                        onLoad={(e) => {
                          const o = orientationFromImgLoad(e);
                          setStripThumbOrient((prev) => (prev[item.id] === o ? prev : { ...prev, [item.id]: o }));
                        }}
                      />
                    ) : null}
                  </div>

                  <div className={s.rubricCaption} style={{ marginTop: 8 }}>
                    <div
                      className={s.rubricMeta}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: '#9b9ba4',
                        fontSize: 12,
                        marginBottom: 4
                      }}
                    >
                      <span
                        className={s.rubricMetaName}
                        style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title="ТОП"
                      >
                        ТОП
                      </span>
                      <time className={s.rubricMetaDate}>
                        {date}
                      </time>
                    </div>

                    <div
                      className={s.rubricCardTitle}
                      style={{
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: 1.25,
                        color: '#111',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                      title={item.title}
                    >
                      {item.title}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ======= РУБРИКИ: переключатель (табы) + одна горизонтальная лента ======= */}
      {allRubrics.length > 0 && (
        <section className={s.rubric}>
          <div
            className={ap.tabs}
            style={{
              '--count': allRubrics.length,
              '--active': activeIndex,
            }}
          >
            {allRubrics.map((r, i) => (
              <button
                key={r.id}
                type="button"
                className={`${ap.tab} ${i === activeIndex ? ap.tabActive : ''}`}
                onClick={() => setActiveRubricId(r.id)}
                style={{
                  width: 'calc(100% / var(--count))',
                  flex: '0 0 calc(100% / var(--count))',
                }}
              >
                {r.name}
              </button>
            ))}

            {/* фиолетовая линия под активной вкладкой */}
            <span
              className={ap.tabIndicator}
              style={{
                width: 'calc(100% / var(--count))',
                transform: 'translateX(calc(var(--active) * 100%))',
              }}
            />
          </div>


          {activeBlock?.items?.length ? (
            <div
              ref={setStripRef(activeBlock.rubric.id)}
              className={s.rubricStrip}
              style={{
                display: 'grid',
                gridAutoFlow: 'column',
                gridAutoColumns: `${RUBRIC_CARD_W}px`,
                gap: 0,
                overflowX: 'auto',
                padding: '0 2px 8px',
                scrollSnapType: 'x none',
                scrollPaddingInline: `calc((100% - ${RUBRIC_CARD_W}px) / 2)`
              }}
              onScroll={() => onStripScroll(activeBlock.rubric.id)}
              onMouseDown={beginStripMouseDrag}
            >
              {[...activeBlock.items, ...activeBlock.items, ...activeBlock.items].map((item, idx) => {
                const img = resolveImageUrl(item.vertical_image_url || item.image_url);
                const date = item.date ? new Date(item.date).toLocaleDateString() : '';
                const key = `${activeBlock.rubric.id}-${item.id}-${idx}`;
                return (
                  <div
                    key={key}
                    className={s.rubricCard}
                    onClick={() => {
                      if (Date.now() < suppressStripCardClickUntilRef.current) return;
                      openNewsByUrl(item.id);
                    }}
                    role="button"
                    style={{ scrollSnapAlign: 'center', scrollSnapStop: 'always', cursor: 'pointer' }}
                  >
                    <div
                      className={`${s.rubricThumbWrap} ${stripThumbOrient[item.id] === 'portrait' ? s.rubricThumbPortrait : s.rubricThumbLandscape}`}
                      style={{ width: '100%', borderRadius: 3, overflow: 'hidden', background: '#eee' }}
                    >
                      {img ? (
                        <img
                          src={img}
                          alt={item.title}
                          loading="eager"
                          onLoad={(e) => {
                            const o = orientationFromImgLoad(e);
                            setStripThumbOrient((prev) => (prev[item.id] === o ? prev : { ...prev, [item.id]: o }));
                          }}
                        />
                      ) : null}
                    </div>

                    <div className={s.rubricCaption} style={{ marginTop: 8 }}>
                      <div
                        className={s.rubricMeta}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          color: '#9b9ba4',
                          fontSize: 12,
                          marginBottom: 4
                        }}
                      >
                        <span
                          className={s.rubricMetaName}
                          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={activeBlock.rubric.name}
                        >
                          {activeBlock.rubric.name}
                        </span>
                        <time className={s.rubricMetaDate}>
                          {date}
                        </time>
                      </div>

                      <div
                        className={s.rubricCardTitle}
                        style={{
                          fontWeight: 500,
                          fontSize: 14,
                          lineHeight: 1.25,
                          color: '#111',
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                        title={item.title}
                      >
                        {item.title}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={s.emptyStrip}>В этой рубрике пока нет новостей</div>
          )}
        </section>
      )}

      <h2 className={s.screenTitle}>Все новости</h2>

      {/* Общая вертикальная лента */}
      <ul className={s.list}>
        {news.map((item) => {
          const date = item.date ? new Date(item.date).toLocaleDateString() : '';
          const imgSrc = resolveImageUrl(item.image_url);
          const preview = (item.content || '').replace(/\n/g, ' ').slice(0, 60);

          const rubricName =
            Array.isArray(item.rubrics) && item.rubrics.length
              ? item.rubrics[0].name
              : 'Лента новостей';

          const unreadQty = unreadMap[item.id] || 0;

          return (
            <li
              key={item.id}
              className={s.row}
              onClick={() => openNewsByUrl(item.id)}
              role="button"
              title={item.title}
            >
              <div
                className={`${s.thumbOuter} ${listThumbOrient[item.id] === 'portrait' ? s.thumbOuterPortrait : ''}`}
              >
                <div className={s.thumbClip}>
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={item.title}
                      className={s.thumb}
                      loading="lazy"
                      onLoad={(e) => {
                        const o = orientationFromImgLoad(e);
                        setListThumbOrient((prev) => (prev[item.id] === o ? prev : { ...prev, [item.id]: o }));
                      }}
                    />
                  ) : (
                    <div className={s.thumbPlaceholder} />
                  )}
                </div>

                {unreadQty > 0 && (
                  <span className={s.commentBadge} aria-label={`Непрочитанных: ${unreadQty}`}>
                    {unreadQty}
                  </span>
                )}
              </div>

              <div className={s.content}>
                <div className={s.meta} onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
                  <span className={s.rubricName}>
                    {rubricName}
                    {isAdmin && (
                      <ActionsMenu
                        open={newsMenuId === item.id}
                        onToggle={(e) => {
                          e.stopPropagation();
                          openNewsMenu(item.id);
                        }}
                        items={[
                          {
                            key: 'edit',
                            label: 'Редактировать',
                            onClick: () => handleEditNewsFromMenu(item),
                          },
                          {
                            key: 'delete',
                            label: 'Удалить',
                            danger: true,
                            onClick: () => deleteNewsItem(item),
                          },
                        ]}
                        title="Действия"
                        align="right"
                        buttonClassName={`${s.pencilBtn} ${s.commentMenuBtn}`}
                      />
                    )}
                  </span>

                  <time className={s.date}>{date}</time>
                </div>

                <div className={s.head}>
                  <h3 className={s.title}>{item.title}</h3>
                </div>

                <div className={s.preview} title={preview}>
                  {preview}{preview.length === 60 ? '…' : ''}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div ref={loadMoreTriggerRef} style={{ height: 1 }} />
      {loadingMore && <div className="loading">Загружаем ещё новости…</div>}

      {modalOpen && selected && (
        <div role="dialog" aria-modal="true" onClick={closeModal} className={s.modalBackdrop}>
          <div
            ref={newsModalRef}
            className={`${s.modalCard} ${activeTab === 'comments' ? s.modalCardComments : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={activeTab === 'comments' ? s.modalMobileCommentsShell : s.modalCardFlow}>
              <div className={activeTab === 'comments' ? s.modalCommentsMobileScroll : s.modalCardFlow}>
            <header className={s.modalHeader}>
              <button className={s.modalClose} onClick={closeModal} aria-label="Закрыть">✕</button>
              <h2 className={s.modalTitleCentered}>{selected.title}</h2>
              <div className={s.modalMeta}>
                <span className={s.modalRubric}>
                  {(Array.isArray(selected.rubrics) && selected.rubrics.length
                    ? selected.rubrics[0].name
                    : 'Лента новостей')}
                </span>
                <time className={s.modalDateInline}>
                  {selected.date ? new Date(selected.date).toLocaleDateString() : ''}
                </time>
              </div>
            </header>

            <div className={s.modalBody}>

              <div className={s.modalArticleCol}>
                <div className={s.modalTabs}>
                  <button
                    type="button"
                    className={`${s.modalTab} ${activeTab === 'news' ? s.modalTabActive : ''}`}
                    onClick={() => setActiveTab('news')}
                  >
                    Новость
                  </button>
                  <button
                    type="button"
                    className={`${s.modalTab} ${activeTab === 'comments' ? s.modalTabActive : ''}`}
                    onClick={() => {
                      setActiveTab('comments');
                      if (comments.length === 0 || (selected && (unreadMap[selected.id] || 0) > 0)) {
                        loadComments(selected.id);
                      } else {
                        const lastId = Math.max(...comments.map(c => Number(c.id) || 0));
                        if (lastId > 0) {
                          apiFetch(`${API_URL}/news/${selected.id}/comments/read`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ up_to_comment_id: lastId }),
                          }).catch(() => { });
                          setUnreadMap(prev => ({ ...prev, [selected.id]: 0 }));
                          setMyLastReadId(prev => Math.max(prev ?? 0, lastId));
                        }
                      }
                    }}
                  >
                    Комментарии
                    {unreadForSelected > 0 && (
                      <span className={s.tabBadge} aria-label={`Непрочитанных: ${unreadForSelected}`}>
                        {unreadForSelected}
                      </span>
                    )}
                  </button>
                </div>

                {selected.image_url ? (
                  <div
                    className={`${s.modalHeroWrap} ${modalHeroOrient === 'portrait' ? s.modalHeroPortrait : s.modalHeroLandscape}`}
                  >
                    <img
                      src={resolveImageUrl(selected.image_url)}
                      alt=""
                      className={s.modalHeroBlur}
                      aria-hidden
                    />
                    <img
                      src={resolveImageUrl(selected.image_url)}
                      alt={selected.title}
                      className={s.modalHeroMain}
                      onLoad={(e) => setModalHeroOrient(orientationFromImgLoad(e))}
                    />
                  </div>
                ) : null}

                {/* Текст статьи — скрыт на мобиле когда открыта вкладка комментариев */}
                <div
                  className={`${s.modalContentFull} ${activeTab === 'comments' ? s.mobileHide : ''}`}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(selected.content || '', {
                      ALLOWED_TAGS: ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'ul', 'ol', 'li', 'a', 'span'],
                      ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
                    })
                  }}
                />
              </div>

              {/* ПРАВАЯ колонка: комментарии.
                  На десктопе — всегда видна сбоку.
                  На мобиле — скрыта когда активна вкладка "Новость". */}
              <div className={`${s.modalCommentsCol} ${activeTab === 'news' ? s.mobileHide : ''}`}>
                <div className={s.modalCommentsHeader}>
                  Комментарии
                  {unreadForSelected > 0 && (
                    <span className={s.tabBadge} aria-label={`Непрочитанных: ${unreadForSelected}`}>
                      {unreadForSelected}
                    </span>
                  )}
                </div>

                <div className={s.commentsScrollArea}>
                  {commentsLoading ? (
                    <div className={s.commentsEmpty}>Загрузка комментариев…</div>
                  ) : comments.length === 0 ? (
                    <div className={s.commentsEmpty}>Пока нет комментариев.</div>
                  ) : (
                    <ul className={s.commentsList}>
                      {comments.map((c) => {
                        const isEditingComment = editingCommentId === c.id;
                        const isCommentMenuOpen = commentMenuId === c.id;
                        const canManageComment = canEditComment(c) || canDeleteComment(c);
                        return (
                          <li key={c.id} id={`news-comment-${c.id}`} className={s.commentRow}>
                            <div className={s.commentAvatarWrap}>
                              <EmployeeTaskStatsAvatar
                                employeeId={c.author_id}
                                stats={commentAuthorStatsMap.get(Number(c.author_id))}
                                fetchStats={false}
                                avatar={c.author_avatar ? (resolveImageUrl(c.author_avatar) || c.author_avatar) : undefined}
                                size={32}
                                alt={c.author_name || 'Гость'}
                              />
                            </div>
                            <div className={s.commentContent}>
                              <div className={`${s.commentBubble} ${isEditingComment ? s.commentBubbleEditing : ''}`}>
                                {canManageComment && !isEditingComment && (
                                  <div className={s.commentMenuWrap}>
                                    <button
                                      type="button"
                                      className={s.commentMenuBtn}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openCommentMenu(c.id);
                                      }}
                                      aria-haspopup="menu"
                                      aria-expanded={isCommentMenuOpen}
                                      aria-label="Действия с комментарием"
                                      title="Действия"
                                    >
                                      <ActionsMenuDotsIcon />
                                    </button>
                                    <DropdownMenu
                                      open={isCommentMenuOpen}
                                      align="left"
                                      items={[
                                        ...(canEditComment(c) ? [{
                                          key: 'edit',
                                          label: 'Редактировать',
                                          onClick: () => startEditComment(c),
                                        }] : []),
                                        ...(canDeleteComment(c) ? [{
                                          key: 'delete',
                                          label: 'Удалить',
                                          danger: true,
                                          onClick: () => deleteComment(c),
                                        }] : []),
                                      ]}
                                    />
                                  </div>
                                )}

                                {c.content?.trim() && (
                                  <div className={s.commentText}>{c.content}</div>
                                )}
                              </div>

                              <div className={s.commentMeta}>
                                <span>{c.author_name || 'Гость'}</span>
                                <span className={s.commentMetaDot}> · </span>
                                <span>{formatCommentTime(c.created_at)}</span>
                                {c.edited_at && (
                                  <>
                                    <span className={s.commentMetaDot}> · </span>
                                    <span>изменён</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                {renderNewsCommentComposer(s.commentComposerFooterDesktopDock)}
              </div>

            </div>
              </div>
              {activeTab === 'comments' && renderNewsCommentComposer(s.commentComposerFooterMobileDock, newsComposerRef)}
            </div>
          </div>
        </div>
      )}

      {/* модалка редактора новостей */}
      {editorOpen && (
        <NewsEditorModal
          mode={editorMode}
          item={editorItem}
          rubrics={allRubrics}
          onClose={() => setEditorOpen(false)}
          onSaved={async () => {
            await Promise.all([loadNews({ append: false, offset: 0 }), loadRubrics(), loadMain(), loadTop()]);
          }}
        />
      )}

      {/* Плавающая кнопка «+» как в админке — fixed справа снизу */}
      {isAdmin && (
        <button
          type="button"
          className={s.newsFab}
          aria-label="Добавить новость"
          title="Добавить новость"
          onClick={openCreateNews}
        >
          <span className={sAdmin.fabSvgWrap} aria-hidden="true">
            <FabBubble className={sAdmin.fabBubble} />
            <div className={sAdmin.fabInner}>
              <NewsPlusIcon className={sAdmin.fabIcon} />
            </div>
          </span>
        </button>
      )}
    </div>
  );
}

export default Home;
